<template>
  <div class="cabinet-page" :class="{ 'is-loading': loading }">
    <app-cells><h1 class="title">{{ $t('loans.title') }}</h1></app-cells>
    <app-button v-if="canRequestLoan" class="btn--stretch" theme="request" @click.native="showRequestModal">{{ $t('loans.request') }}</app-button>
    <div v-else-if="eligibilityReason === 'minimum_tenure'" class="loan-policy loan-policy--warning">
      {{ $t('loans.tenure') }}
    </div>
    <div v-else-if="eligibilityReason === 'active_loan'" class="loan-policy loan-policy--warning">
      {{ $t('loans.activeBlocked') }}
    </div>
    <p class="loan-policy">{{ $t('loans.oneActive') }}</p>

    <app-page-loader v-if="loading" />
    <cabinet-state
      v-else-if="loadError"
      class="loan-empty"
      type="error"
      :title="$t('loans.unavailable')"
      :message="$t('common.pleaseRetry')"
      @retry="loadLoans"
    />
    <template v-else>
      <section v-if="currentLoans.length" class="loan-group">
        <h2 class="loan-group__title loan-group__title--current">{{ $t('common.current') }}</h2>
        <loan-card
          v-for="loan in currentLoans"
          :key="loan.id"
          :loan="loan"
          :downloading="Boolean(downloading[loan.id])"
          @cancel="showCancelModal"
          @download="downloadSchedule"
        />
      </section>
      <section v-if="historyLoans.length" class="loan-group">
        <h2 class="loan-group__title loan-group__title--history">{{ $t('common.history') }}</h2>
        <loan-card
          v-for="loan in historyLoans"
          :key="loan.id"
          :loan="loan"
          :downloading="Boolean(downloading[loan.id])"
          @download="downloadSchedule"
        />
      </section>
      <cabinet-state
        v-if="!loans.length"
        class="loan-empty"
        type="empty"
        :title="$t('common.noRequests')"
        :message="$t('common.requestsAppear')"
      />
    </template>

    <modal
      name="request-loan"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      :shiftY="0"
      transition="pop-out"
    >
      <div class="popup">
        <button @click="$modal.hide('request-loan')" class="popup__close" type="button" :aria-label="$t('common.close')"></button>
        <div class="popup__title">{{ $t('loans.newRequest') }}</div>
        <div class="popup__body">
          <loan-request-form :key="requestFormKey" @created="onCreated" />
        </div>
      </div>
    </modal>

    <modal
      name="cancel-loan"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      :shiftY="0"
      transition="pop-out"
    >
      <div class="popup">
        <button @click="$modal.hide('cancel-loan')" class="popup__close" type="button" :aria-label="$t('common.close')"></button>
        <div class="popup__title">{{ $t('loans.cancelTitle') }}</div>
        <div class="popup__body">
          <div class="popup__info">{{ $t('advances.moveHistory') }}</div>
          <app-cells position="center" :indent="false" :wrap="false">
            <app-button theme="dark" @click.native="$modal.hide('cancel-loan')">{{ $t('common.keepRequest') }}</app-button>
            <app-button ref="cancel" theme="error" :disabled="cancelling" @click.native="cancelLoan">
              {{ $t('common.cancelRequest') }}
            </app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  cancelPersonalCabinetStaffLoan,
  getPersonalCabinetStaffLoanPdf,
  getPersonalCabinetStaffLoans,
} from '@/http'
import LoanCard from './LoanCard'
import LoanRequestForm from './LoanRequestForm'
import CabinetState from './CabinetState'

const HISTORY_STATUSES = ['rejected', 'cancelled', 'fully_paid', 'abandoned']

export default {
  name: 'PersonalCabinetLoans',
  components: { LoanCard, LoanRequestForm, CabinetState },
  data() {
    return {
      loading: true,
      loadError: false,
      loans: [],
      requestFormKey: 0,
      cancelling: false,
      loanToCancel: null,
      downloading: {},
    }
  },
  computed: {
    eligibility() {
      return this.$store.state.personalCabinetData?.employee?.staff_loan_eligibility || {}
    },
    eligibilityReason() {
      return this.eligibility.reason?.code || this.eligibility.reason_code || null
    },
    hasActiveLoan() {
      return this.loans.some(loan => ['approved', 'disbursed'].includes(loan.status.code))
    },
    canRequestLoan() {
      return this.eligibility.can_request !== false && !this.hasActiveLoan
    },
    currentLoans() {
      return this.loans.filter(loan => !HISTORY_STATUSES.includes(loan.status.code))
    },
    historyLoans() {
      return this.loans.filter(loan => HISTORY_STATUSES.includes(loan.status.code))
    },
  },
  created() {
    this.loadLoans()
  },
  methods: {
    loadLoans() {
      this.loading = true
      this.loadError = false
      return getPersonalCabinetStaffLoans()
        .then(response => {
          this.loans = response.data
        })
        .catch(() => {
          this.loadError = true
        })
        .finally(() => {
          this.loading = false
        })
    },
    showRequestModal() {
      this.requestFormKey += 1
      this.$modal.show('request-loan')
    },
    onCreated() {
      this.$modal.hide('request-loan')
      this.$root.$emit('personal-cabinet:requests-changed')
      this.loadLoans()
    },
    showCancelModal(loan) {
      this.loanToCancel = loan
      this.$modal.show('cancel-loan')
    },
    cancelLoan() {
      if (!this.loanToCancel || this.cancelling) return
      this.cancelling = true
      this.$refs.cancel.preload = true
      cancelPersonalCabinetStaffLoan(this.loanToCancel.id)
        .then(response => {
          const index = this.loans.findIndex(loan => loan.id === response.data.id)
          if (index !== -1) this.$set(this.loans, index, response.data)
          this.$modal.hide('cancel-loan')
          this.loanToCancel = null
          this.$root.$emit('personal-cabinet:requests-changed')
        })
        .finally(() => {
          this.cancelling = false
          if (this.$refs.cancel) this.$refs.cancel.preload = false
        })
    },
    downloadSchedule(loan) {
      if (this.downloading[loan.id]) return
      const pdfWindow = window.open('', '_blank')
      this.$set(this.downloading, loan.id, true)
      getPersonalCabinetStaffLoanPdf(loan.id)
        .then(response => {
          const url = response.data && response.data.url
          if (!url) throw new Error('Staff loan PDF URL is missing')
          if (pdfWindow) pdfWindow.location.replace(url)
          else window.location.assign(url)
        })
        .catch(() => {
          if (pdfWindow) pdfWindow.close()
          this.$notify({ type: 'error', title: this.$t('loans.pdfError') })
        })
        .finally(() => {
          this.$set(this.downloading, loan.id, false)
        })
    },
  },
}
</script>

<style lang="sass" scoped>
.loan-group
  margin-top: 26px

.loan-group__title
  margin-bottom: 12px
  padding: 9px 12px
  border-left: 4px solid
  border-radius: 0 8px 8px 0
  font-size: 14px
  font-weight: 600
  letter-spacing: 1px
  text-transform: uppercase

.loan-group__title--current
  border-color: $color-theme
  background-color: #f1f7e9
  color: #4f8d12

.loan-group__title--history
  border-color: #8a8a8a
  background-color: #f3f3f3
  color: #5f5f5f

.loan-empty
  margin-top: 24px

.loan-policy
  margin: 10px 0 0
  padding: 12px 14px
  border-radius: 10px
  background: #f4f1e9
  color: #6f6b61
  font-size: 12px
  line-height: 1.45

.loan-policy--warning
  background: #fff4dc
  color: #7e6226

</style>
